import React, { useContext, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import postService from '../../../services/postService'
import { enqueueSnackbar } from 'notistack'
import PostCardHeader from '../HomePage/PostCard/PostCardHeader'
import CardContent from '@mui/material/CardContent'
import {
  CardActions,
  CardMedia, Grid,
  IconButton, Paper,
  TextField, ToggleButton, Typography
} from '@mui/material'
import ChatIcon from '@mui/icons-material/Chat'
import ShareIcon from '@mui/icons-material/Share'
import Card from '@mui/material/Card'
import { AuthTokenContext } from '../../contexts/AuthTokenContext'
import { FormProvider, useForm } from 'react-hook-form'
import commentService from '../../../services/commentService'
import { UserInfoContext } from '../../contexts/UserInfoContext'
import Comment from './Comment/Comment'
import SubmitButton from '../../commonUIComponents/SubmitButton'
import { SearchBarContext } from '../../contexts/SearchBarContext'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import voteService from '../../../services/voteService'
import VoteToggleButtonGroup from '../../commonUIComponents/VoteToggleButtonGroup'

const ViewPostPage = () => {
  const { authToken } = useContext(AuthTokenContext)
  const { userInfo } = useContext(UserInfoContext)
  const { setCommunityId } = useContext(SearchBarContext)
  const [searchParams] = useSearchParams()
  const { postId } = useParams()
  const highlightedCommentId = searchParams.get('highlightedCommentId')
  const [post, setPost] = useState()
  const [comments, setComments] = useState()
  const [vote, setVote] = useState(null)
  const methods = useForm({ mode: 'onChange' })
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = methods

  useEffect(() => {
    postService.getPostById(postId)
      .then(res => {
        setPost(res)
        setCommunityId(res.communityId)
      })
      .catch(err => enqueueSnackbar(err.response.data.message, { variant: 'error' }))
  }, [postId])

  useEffect(() => {
    fetchComments()
  }, [])

  useEffect(() => {
    voteService.getVoteByPostId(postId)
      .then(res => {
        if (res.vote === -1) {
          setVote('dislike')
        } else if (res.vote === 0) {
          setVote(null)
        } else if (res.vote === 1) {
          setVote('like')
        }
      })
      .catch(err => enqueueSnackbar(err.response?.data?.message, { variant: 'error' }))
  }, [])

  const toggleOnChangeHandler = (event, value) => {
    voteService.updatePostVote(postId, value)
      .then(res => {
        const currentPost = post
        currentPost.myVote = res.myVote
        currentPost.votes = res.votes
        setPost(currentPost)
        setVote(value)
      })
  }

  const submitOnSuccess = () => {
    fetchComments()
  }

  const fetchComments = () => {
    commentService.getCommentsByPostId(postId)
      .then(res => setComments(res))
      .catch(err => console.log(err))
  }

  const updateCommentVote = (commentId, userVote, totalVotes) => {
    const currentComments = [...comments]
    currentComments.forEach(comment => {
      if (comment.id === commentId) {
        comment.myVote = userVote
        comment.votes = totalVotes
      }
    })
    setComments(currentComments)
  }

  const createCommentSubmitHandler = values => {
    values.userId = userInfo.id
    commentService.createComment(postId, values)
      .then(methods.reset())
      .then(fetchComments)
      .then(res => {
        enqueueSnackbar('Successfully added your comment!', { variant: 'success' })
      })
      .catch(err => enqueueSnackbar(err.response?.data.message, { variant: 'error' }))
  }

  return post
    ? (
      <Grid container justifyContent="space-between">
        <Grid item xs>
          <Card sx={{ marginY: 2 }}>
            <PostCardHeader authToken={authToken} post={post} fetchPosts={fetchComments} />
            <CardContent sx={{ textDecoration: 'none', padding: 0 }}>
              <Typography variant="h6" color="common.white" component="p" sx={{ paddingLeft: '1.5rem' }}>
                {post?.title}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                component="p"
                sx={{ pl: '1.5rem' }}
                dangerouslySetInnerHTML={{ __html: post?.body }}
              />
              <Typography variant="body1" color="text.secondary" component="p" sx={{ paddingLeft: '1.5rem' }}>
                {post?.link}
              </Typography>
              {post?.images?.map(image => (
                <CardMedia
                  key={image}
                  component="img"
                  sx={{ display: 'flex', objectFit: 'contain', paddingTop: '1rem' }}
                  height="500"
                  image={image}
                  alt="image"
                />
              ))}
            </CardContent>
            <CardActions disableSpacing>
              <VoteToggleButtonGroup
                value={vote}
                exclusive
                onChange={toggleOnChangeHandler}
                sx={{ ml: '0.5rem' }}
                disabled = {!authToken}
              >
                <ToggleButton value="like" aria-label="like">
                  <KeyboardArrowUpOutlinedIcon />
                </ToggleButton>
                <ToggleButton value="" aria-label="voteCount" disabled>
                  <Typography variant="subtitle2">{post.votes}</Typography>
                </ToggleButton>
                <ToggleButton value="dislike" aria-label="dislike">
                  <KeyboardArrowDownOutlinedIcon />
                </ToggleButton>
              </VoteToggleButtonGroup>
              {/* <IconButton aria-label="comment"> */}
              {/*  <ChatIcon /> */}
              {/* </IconButton> */}
              <IconButton aria-label="share">
                <ShareIcon />
              </IconButton>
            </CardActions>
          </Card>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(createCommentSubmitHandler)}>
              <TextField
                {...register('body', { required: { value: true, message: 'Add a comment' } })}
                fullWidth
                multiline
                InputProps={{
                  endAdornment:
                    <>
                      <SubmitButton buttonLabel="Comment" loadingLabel="Commenting"/>
                    </>
                }}
                sx={{ marginBottom: '1.5rem' }}
              />
            </form>
          </FormProvider>
          {comments?.map(comment => <Comment key={comment.id} authToken={authToken} comment={comment} highlighted={comment.id === parseInt(highlightedCommentId)} postId={postId} updateCommentVote={updateCommentVote} submitOnSuccess={submitOnSuccess} />)}
        </Grid>
        {/* <Grid item xs sx={{ display: { xs: 'none', md: 'block' } }}> */}
        {/*  <Paper> */}
        {/*    <Typography>Community Info will be added here</Typography> */}
        {/*  </Paper> */}
        {/* </Grid> */}
      </Grid>
      )
    : (
      <></>
      )
}

export default ViewPostPage
